'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Copy, CheckCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

const demoAccounts = [
  { role: 'User', email: 'sarah@brandco.com', password: 'BrandCo2026!' },
  { role: 'Admin', email: 'admin@instaextract.io', password: 'AdminPanel2026!' },
];

interface LoginFormProps {
  onForgotPassword: () => void;
  onRegister: () => void;
}

export default function LoginForm({ onForgotPassword, onRegister }: LoginFormProps) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({ defaultValues: { remember: false } });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = (account: typeof demoAccounts[0]) => {
    setValue('email', account.email);
    setValue('password', account.password);
  };

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const userMeta = result?.user?.user_metadata;
      const appMeta = result?.user?.app_metadata;
      const isAdmin =
        userMeta?.role === 'admin' || appMeta?.role === 'admin';

      toast.success(`Welcome back! Redirecting to ${isAdmin ? 'Admin' : 'User'} dashboard...`);
      router.push(isAdmin ? '/admin-dashboard' : '/user-dashboard');
    } catch (err: any) {
      setLoading(false);
      const msg = err?.message || 'Invalid credentials. Please try again.';
      setError('email', { message: msg });
    }
  };

  return (
    <div className="slide-up">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to your InstaExtract account</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Email Address
          </label>
          <input
            type="email"
            className="input-field"
            placeholder="you@company.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className="input-field pr-10"
              placeholder="Your password"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-border text-primary focus:ring-ring"
              {...register('remember')}
            />
            <span className="text-sm text-muted-foreground">Remember me</span>
          </label>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary font-medium hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Don&apos;t have an account?{' '}
        <button onClick={onRegister} className="text-primary font-semibold hover:underline">
          Create one free
        </button>
      </p>

      {/* Demo credentials */}
      <div className="mt-8 border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 bg-muted/50 border-b border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Demo Accounts</p>
        </div>
        <div className="divide-y divide-border">
          {demoAccounts.map((account) => (
            <div key={`demo-${account.role}`} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary mb-1">
                  {account.role}
                </span>
                <p className="text-xs text-muted-foreground truncate">{account.email}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleCopy(account.email, `${account.role}-email`)}
                  className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  title="Copy email"
                >
                  {copiedField === `${account.role}-email` ? <CheckCheck size={13} className="text-emerald-500" /> : <Copy size={13} />}
                </button>
                <button
                  type="button"
                  onClick={() => autofill(account)}
                  className="text-xs font-medium text-primary hover:underline px-2 py-1 rounded hover:bg-primary/5 transition-colors"
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}